import * as React from 'react';
import { Controller, useController } from 'react-hook-form';
import { Undo2 } from 'lucide-react';
import { END_AFTER_START_MESSAGE } from '@ooh/contracts';
import type { Client } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { CampaignDurationField } from '../../offers/create-fields/campaign-duration-field';
import { BrandField } from '../../offers/create-fields/brand-field';
import { CampaignNumberPreviewField } from '../../offers/create-fields/campaign-number-preview-field';
import { CoCreatorsCheckbox } from '../../offers/create-fields/co-creators-checkbox';
import { useInlineClientCreateAction } from '../../offers/create-fields/use-inline-client-create-action';
import type {
  QuickAddCreateDraftFormValues,
  QuickAddClientOption,
  QuickAddOfferPickerProps,
} from './quick-add-offer-picker';
import type { QuickAddOfferPickerPanelProps } from './quick-add-offer-picker-panel';
import { SectionLabel } from './quick-add-offer-list';

const INLINE_CLIENT_TEST_ID = 'quick-add-client-select';
const SALESPERSON_TEST_ID = 'quick-add-salesperson-select';
const BRAND_TEST_ID = 'quick-add-brand-select';
const QUICK_ADD_BRAND_FIELD_TEST_ID = 'quick-add-field-brand';
const CAMPAIGN_DURATION_TEST_ID = 'quick-add-campaign-duration';

function getCreateFieldError(
  field: keyof QuickAddCreateDraftFormValues,
  message: string | undefined,
  labels: Pick<QuickAddOfferPickerProps, 'validationClientRequiredLabel'>,
) {
  if (!message) return null;

  if (field === 'clientId' && message === 'clientRequired') {
    return labels.validationClientRequiredLabel;
  }

  return message;
}
/** Shared by the client and draft-name fields; extracted to keep CreateNewSection's complexity down. */
function CreateFieldErrorMessage({ testId, message }: { testId: string; message: string | null }) {
  if (!message) return null;
  return (
    <p className="text-xs text-destructive" data-testid={testId}>
      {message}
    </p>
  );
}

/** Extracted to keep CreateNewSection's complexity down — renders nothing until a duration label is provided. */
function CreateNewCampaignDurationSection({
  campaignDurationLabel,
  campaignDurationPlaceholder,
  dateRangeErrorLabel,
  fromField,
  toField,
}: {
  campaignDurationLabel: string | undefined;
  campaignDurationPlaceholder: string | undefined;
  dateRangeErrorLabel: string | undefined;
  fromField: ReturnType<
    typeof useController<QuickAddCreateDraftFormValues, 'defaultDateFrom'>
  >['field'];
  toField: ReturnType<
    typeof useController<QuickAddCreateDraftFormValues, 'defaultDateTo'>
  >['field'];
}) {
  if (!campaignDurationLabel) return null;
  return (
    <CampaignDurationField
      id={CAMPAIGN_DURATION_TEST_ID}
      fieldTestId="field-campaign-duration"
      label={campaignDurationLabel}
      placeholder={campaignDurationPlaceholder}
      fromValue={fromField.value}
      toValue={toField.value}
      error={dateRangeErrorLabel}
      onChange={({ from, to }) => {
        fromField.onChange(from);
        toField.onChange(to);
      }}
    />
  );
}

/** Extracted to keep CreateNewSection's complexity down — renders nothing unless the caller wired the trigger. */
function CreateNewCoCreatorsSection({
  addCoCreators,
  onAddCoCreatorsChange,
}: {
  addCoCreators: boolean;
  onAddCoCreatorsChange?: (checked: boolean) => void;
}) {
  if (!onAddCoCreatorsChange) return null;
  return (
    <CoCreatorsCheckbox
      checked={addCoCreators}
      onCheckedChange={onAddCoCreatorsChange}
      testId="quick-add-co-creators"
    />
  );
}

export function CreateNewSection({
  createNewLabel,
  openFullFormLabel,
  openFullFormHint,
  clientFieldLabel,
  clientFieldPlaceholder,
  salespersonFieldLabel,
  salespersonFieldPlaceholder,
  salespersonFieldLockedHint,
  salespersonFieldDisabled = false,
  campaignDurationLabel,
  campaignDurationPlaceholder,
  campaignDurationInvalidRangeLabel,
  clientOptions,
  selectedClientBrands = [],
  canCreateClient = false,
  onClientCreated,
  addCoCreators = false,
  onAddCoCreatorsChange,
  salesUserOptions,
  validationClientRequiredLabel,
  backToExistingLabel,
  createDraftForm,
  onBack,
  onSubmit,
  onOpenFullForm,
  campaignNumberPreview,
}: {
  createNewLabel: string;
  openFullFormLabel: string;
  openFullFormHint: string;
  clientFieldLabel: string;
  clientFieldPlaceholder: string;
  salespersonFieldLabel: string;
  salespersonFieldPlaceholder: string;
  salespersonFieldLockedHint: string;
  /** True when the caller is sales-department but lacks offer.assign_other_salesperson. */
  salespersonFieldDisabled?: boolean;
  campaignDurationLabel?: string;
  campaignDurationPlaceholder?: string;
  campaignDurationInvalidRangeLabel?: string;
  clientOptions: QuickAddClientOption[];
  /** The selected client's brands, or an empty array when no client is selected. */
  selectedClientBrands?: string[];
  /** Whether the current user has the client.create permission. */
  canCreateClient?: boolean;
  onClientCreated?: (client: Client, wasCreated: boolean) => void;
  addCoCreators?: boolean;
  onAddCoCreatorsChange?: (checked: boolean) => void;
  salesUserOptions: QuickAddClientOption[];
  validationClientRequiredLabel: string;
  backToExistingLabel: string;
  createDraftForm: QuickAddOfferPickerPanelProps['createDraftForm'];
  onBack: () => void;
  onSubmit?: () => void;
  onOpenFullForm?: () => void;
  /** See QuickAddOfferPickerProps.campaignNumberPreview — undefined renders no preview field (apps/mockups). */
  campaignNumberPreview?: QuickAddOfferPickerProps['campaignNumberPreview'];
}) {
  const { field: defaultDateFromField } = useController({
    control: createDraftForm.control,
    name: 'defaultDateFrom',
  });
  const { field: defaultDateToField } = useController({
    control: createDraftForm.control,
    name: 'defaultDateTo',
  });
  const dateRangeError = createDraftForm.formState.errors.defaultDateTo?.message;
  const dateRangeErrorLabel =
    dateRangeError === END_AFTER_START_MESSAGE ? campaignDurationInvalidRangeLabel : dateRangeError;
  const { action: createClientAction, dialog: createClientDialog } = useInlineClientCreateAction({
    canCreateClient,
    onClientCreated: (client, wasCreated) => onClientCreated?.(client, wasCreated),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>{createNewLabel}</SectionLabel>
        <Button
          type="button"
          variant="ghost"
          className="h-auto gap-1.5 px-0 py-0 text-xs text-muted-foreground"
          onClick={onBack}
        >
          <Undo2 className="h-3.5 w-3.5" />
          {backToExistingLabel}
        </Button>
      </div>

      <form
        id="quick-add-offer-create-form"
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor={INLINE_CLIENT_TEST_ID} className="text-sm font-medium text-foreground">
            {clientFieldLabel}
          </Label>
          <Controller
            control={createDraftForm.control}
            name="clientId"
            render={({ field }) => (
              <SearchableSelectField
                id={INLINE_CLIENT_TEST_ID}
                data-testid={INLINE_CLIENT_TEST_ID}
                options={clientOptions}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  createDraftForm.clearErrors('clientId');
                }}
                placeholder={clientFieldPlaceholder}
                error={Boolean(createDraftForm.formState.errors.clientId)}
                action={createClientAction}
              />
            )}
          />
          <CreateFieldErrorMessage
            testId="field-client-error"
            message={getCreateFieldError(
              'clientId',
              createDraftForm.formState.errors.clientId?.message,
              { validationClientRequiredLabel },
            )}
          />
        </div>
        {createClientDialog}
        <Controller
          control={createDraftForm.control}
          name="brand"
          render={({ field }) => (
            <BrandField
              brandValue={field.value}
              onBrandChange={field.onChange}
              brandError={createDraftForm.formState.errors.brand?.message}
              selectedClientBrands={selectedClientBrands}
              id={BRAND_TEST_ID}
              fieldTestId={QUICK_ADD_BRAND_FIELD_TEST_ID}
            />
          )}
        />
        <div className="space-y-1.5">
          <Label htmlFor={SALESPERSON_TEST_ID} className="text-sm font-medium text-foreground">
            {salespersonFieldLabel}
          </Label>
          <DisabledHintTooltip
            disabled={salespersonFieldDisabled}
            hint={salespersonFieldLockedHint}
          >
            <Controller
              control={createDraftForm.control}
              name="salespersonId"
              render={({ field }) => (
                <SearchableSelectField
                  id={SALESPERSON_TEST_ID}
                  data-testid={SALESPERSON_TEST_ID}
                  disabled={salespersonFieldDisabled}
                  options={salesUserOptions}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                  placeholder={salespersonFieldPlaceholder}
                />
              )}
            />
          </DisabledHintTooltip>
        </div>
        {campaignNumberPreview ? (
          <CampaignNumberPreviewField preview={campaignNumberPreview} />
        ) : null}
        <CreateNewCampaignDurationSection
          campaignDurationLabel={campaignDurationLabel}
          campaignDurationPlaceholder={campaignDurationPlaceholder}
          dateRangeErrorLabel={dateRangeErrorLabel}
          fromField={defaultDateFromField}
          toField={defaultDateToField}
        />
        <CreateNewCoCreatorsSection
          addCoCreators={addCoCreators}
          onAddCoCreatorsChange={onAddCoCreatorsChange}
        />
        <div className="rounded-lg border border-dashed border-border/60 px-3 py-3 text-sm text-muted-foreground">
          <Button
            type="button"
            variant="inline"
            size="xs"
            className="px-0"
            onClick={onOpenFullForm}
          >
            {openFullFormLabel}
          </Button>
          <p className="mt-1 text-2xs leading-5 text-muted-foreground">{openFullFormHint}</p>
        </div>
      </form>
    </div>
  );
}
